"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useConfigEnv from "../../../zustand";

type Props = {
  id: number;
  handleOutsideClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleCloseDetailPopup: () => void;
  // Navigation props
  navigationStack?: Array<{ id: number; type: "payment" | "refund"; name: string }>;
  onBack?: () => void;
  isAccountMove?: boolean;
};

const formatDateTime = (dateString: string, useSlash = false): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  if (useSlash) {
    return `${year}/${month}/${day} - ${hours}:${minutes}:${seconds}`;
  }
  return `${day}-${month}-${year} - ${hours}:${minutes}:${seconds}`;
};

const addHours = (dateString: any, hours: number): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  date.setHours(date.getHours() + hours);
  return date.toISOString();
};

const RefundDetail = (props: Props) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { id, handleOutsideClick, handleCloseDetailPopup, navigationStack, onBack, isAccountMove = false } = props;
  const [refundDetail, setRefundDetail] = useState<any>();
  const [loading, setLoading] = useState(true);

  const { env }: any = useConfigEnv();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${env?.apiCall}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: isAccountMove ? "account.move" : "refund.payment",
            method: "web_read",
            ids: [id],
            with_context: {
              lang: language == "vi" ? "vi_VN" : "en_US",
              tz: "Asia/Saigon",
            },
            kwargs: {
              specification: isAccountMove
                ? {
                  name: {},
                  amount_total: {},
                  create_date: {},
                  invoice_payments_widget: {},
                  create_uid: {
                    fields: {
                      display_name: {},
                    },
                  },
                  narration: {},
                }
                : {
                  name: {},
                  amount: {},
                  refund_method: {},
                  create_date: {},
                  create_uid: {
                    fields: {
                      display_name: {},
                    },
                  },
                  note: {},
                  refund_source_summary: {},
                },
            },
          }),
        });
        if (response.status === 200) {
          const res = await response.json();
          const data = res[0];
          if (isAccountMove) {
            let paymentsWidget = data?.invoice_payments_widget;
            if (typeof paymentsWidget === "string") {
              try {
                paymentsWidget = JSON.parse(paymentsWidget);
              } catch (e) {
                console.error("Error parsing invoice_payments_widget:", e);
              }
            }
            const journalName = paymentsWidget?.content?.[0]?.journal_name;
            setRefundDetail({
              id: data?.id,
              name: data?.name,
              amount: data?.amount_total,
              refundMethod: journalName,
              createDate: formatDateTime(addHours(data?.create_date || false, 7), true),
              creatorName: data?.create_uid?.display_name,
              reason: data?.narration || t("none"),
              refund_source_summary: [],
            });
          } else {
            setRefundDetail({
              id: data?.id,
              name: data?.name,
              amount: data?.amount,
              refundMethod: data?.refund_method,
              createDate: formatDateTime(addHours(data?.create_date || false, 7), false),
              creatorName: data?.create_uid?.display_name,
              reason: data?.note || t("none"),
              refund_source_summary: data?.refund_source_summary || [],
            });
          }
        } else {
          console.error("Error fetching refund data:", response.status);
        }
      } catch (error) {
        console.error("Error fetching refund data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, language, env?.apiCall, isAccountMove, t]);

  return (
    <>
      <div
        className="fixed z-[999999999] inset-0 flex items-center justify-center bg-black bg-opacity-50 px-2"
        onClick={handleOutsideClick}
      >
        <div className="flex flex-col items-center gap-6 rounded-2xl h-[50vh] overflow-y-auto bg-white md:p-6 p-[32px] min-[320px] max-w-2xl w-full shadow-lg relative">
          <button
            onClick={handleCloseDetailPopup}
            className="absolute top-4 right-5 text-[#A4A4A4] hover:text-gray-600 text-[22px] z-10"
          >
            &times;
          </button>

          {navigationStack && navigationStack.length > 0 && (
            <div className="w-full flex justify-start -mt-2 px-1">
              <span
                onClick={onBack}
                className="text-[#20B73B] font-medium cursor-pointer hover:underline text-xs md:text-sm flex items-center gap-1 border-b border-dashed border-[#20B73B]"
              >
                &lt; {navigationStack[navigationStack.length - 1].type === "refund"
                  ? t("back_to_refund_receipt", { code: navigationStack[navigationStack.length - 1].name })
                  : t("back_to_payment_receipt", { code: navigationStack[navigationStack.length - 1].name })}
              </span>
            </div>
          )}

          <h2 className="text-xl text-[#2D2D2D] font-bold text-center w-full mt-2">
            {t("refund_receipt_details", { code: refundDetail?.name || "" })}
          </h2>

          {loading ? (
            <div className="flex justify-center items-center py-20 w-full">
              <div className="w-[48px] h-[48px] animate-spin">
                <img
                  src="/images/payment/loading.png"
                  alt="loading icon"
                  width={48}
                  height={48}
                />
              </div>
            </div>
          ) : refundDetail ? (
            <div className="flex flex-col gap-6 w-full">
              <div className="border border-[#EBEBEB] rounded-lg md:p-6 p-[16px] min-[320px]:p-[6px] w-full">
                <div className="flex flex-col gap-3 w-full">
                  {/* Row 1: Mã giao dịch (Transaction Code) */}
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("transaction_code")}
                    </span>
                    <span className="font-semibold text-text-primary text-[12px] md:text-[14px]">
                      {refundDetail.name}
                    </span>
                  </div>

                  {/* Row 2 (if isAccountMove): Thời gian tạo (Created Time) */}
                  {isAccountMove && (
                    <div className="flex justify-between items-center w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("time_create")}
                      </span>
                      <div className="flex items-center border border-[#D8D8D8] px-2 py-0.5 rounded-2xl">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="w-4 h-4 mr-2 text-[#666666]"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        <span className="text-[#666666] text-[12px] md:text-[14px]">
                          {refundDetail.createDate}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Row 3: Phương thức thanh toán (Payment Method) / Phương thức hoàn tiền (Refund Method) */}
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {isAccountMove ? t("payment_method") : t("refund_method")}
                    </span>
                    <span className="text-[#20B73B] border border-[#20B73B] px-2 py-0.5 rounded-[8px] text-[12px] md:text-[14px]">
                      {refundDetail.refundMethod === "cash"
                        ? t("refund_method_cash")
                        : refundDetail.refundMethod === "transfer"
                          ? t("refund_method_transfer")
                          : refundDetail.refundMethod || t("none")}
                    </span>
                  </div>

                  {/* Row 2 (if NOT isAccountMove): Thời gian tạo (Created Time) */}
                  {!isAccountMove && (
                    <div className="flex justify-between items-center w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("time_create")}
                      </span>
                      <div className="flex items-center border border-[#D8D8D8] px-2 py-0.5 rounded-2xl">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="w-4 h-4 mr-2 text-[#666666]"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        <span className="text-[#666666] text-[12px] md:text-[14px]">
                          {refundDetail.createDate}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Row 4: Số tiền (Amount) / Số tiền hoàn (Refund Amount) */}
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {isAccountMove ? t("amount") : t("refund_amount")}
                    </span>
                    <span className={`text-[12px] md:text-[14px] ${isAccountMove ? "font-semibold text-text-primary" : "font-bold text-[#333]"}`}>
                      {Number(refundDetail.amount).toLocaleString("vi-VN")}{isAccountMove ? " VND" : "đ"}
                    </span>
                  </div>

                  {/* Row 5 (if NOT isAccountMove): Người tạo (Creator) */}
                  {!isAccountMove && (
                    <div className="flex justify-between items-center w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("creator")}
                      </span>
                      <span className="text-[12px] md:text-[14px] text-[#2D2D2D] font-medium">
                        {refundDetail.creatorName}
                      </span>
                    </div>
                  )}

                  {/* Row 6 (if NOT isAccountMove): Lý do (Reason) */}
                  {!isAccountMove && (
                    <div className="flex justify-between items-start w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("reason")}
                      </span>
                      <span className="text-[12px] md:text-[14px] text-[#2D2D2D] w-1/2 flex justify-end text-right">
                        {refundDetail.reason}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Applied Outstanding/Balances list (if NOT isAccountMove) */}
              {/* {!isAccountMove && (
                <div className="flex flex-col gap-3 w-full">
                  <h4 className="text-base font-bold text-[#2D2D2D] text-left">
                    {t("list_of_outstanding") || "Danh sách khoản dư áp dụng"}
                  </h4>
                  <div className="w-full border border-[#EBEBEB] rounded-lg overflow-hidden bg-white text-sm">
                    <div className="grid grid-cols-2 bg-[#F5F7FA] border-b border-[#EBEBEB] p-3 font-semibold text-[#666]">
                      <div className="text-left">{t("invoice_code") || t("voucher_code_v1") || "Mã chứng từ"}</div>
                      <div className="text-right">{t("amount") || "Số tiền"}</div>
                    </div>
                    {refundDetail.refund_source_summary?.length > 0 ? (
                      refundDetail.refund_source_summary.map((item: any, index: number) => (
                        <div key={index} className="grid grid-cols-2 border-b border-[#EBEBEB] last:border-b-0 p-3 text-[#2D2D2D]">
                          <div className="text-left font-medium">{item.name}</div>
                          <div className="text-right font-bold text-[#2D2D2D]">{Math.abs(item.amount_total || 0).toLocaleString("vi-VN")}đ</div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-400">{t("no_data")}</div>
                    )}
                  </div>
                </div>
              )} */}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">{t("no_data")}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default RefundDetail;
